import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@/components/atoms/Spinner';
import PageLayout from '@/components/Layout/PageLayout';
import { useCage, useCageName } from '@/contexts/cage';
import { useCageFilter } from '@/contexts/cage-filter-context';
import { useGetFullWelfare } from '@/domain/welfare/useGetFullWelfare';
import { useDateRangePicker } from '@/hooks/useDateRangePicker';
import { CageWelfareMaturationPageContent } from '@/routes/Client/Cage/pages/CageWelfare/CageWelfareMaturation/CageWelfareMaturationPageContent';
import { HEALTH_DEFAULT_DATE_RANGE_PRESET_ID } from '@/routes/Client/Cage/pages/Health/components/healthDetailChartConstants';
import type { CagePageInterface } from '@/routes/Client/Cage/utils/cage-pages-interface';

function CageWelfareMaturationContainer({ clientId, locationId, cageId }: CagePageInterface) {
    const { t } = useTranslation();

    const { loading: cageLoading } = useCage();
    const cageName = useCageName();
    const { daypartTag, selectedFishGroupId } = useCageFilter();
    const { dateFrom, dateTo, onDateChange } = useDateRangePicker({
        key: 'maturation',
        defaultPresetId: HEALTH_DEFAULT_DATE_RANGE_PRESET_ID,
    });

    const welfareParams = useMemo(
        () => ({
            from: dateFrom,
            to: dateTo,
            daypartTag: daypartTag || 'day',
            fishGroupId: selectedFishGroupId,
        }),
        [dateFrom, dateTo, daypartTag, selectedFishGroupId]
    );

    const { data: fishHealth, isLoading: fishHealthLoading } = useGetFullWelfare(
        cageId,
        welfareParams,
        {
            enabled: Boolean(dateFrom && dateTo),
            staleTime: 5 * 60 * 1000,
        }
    );

    const loading = cageLoading || fishHealthLoading;

    if (loading || !dateFrom || !dateTo) {
        return <Spinner size="lg" fullScreen />;
    }

    return (
        <PageLayout title={cageName} subtitle={t('cageWelfare.maturation.maturation')}>
            <CageWelfareMaturationPageContent
                clientId={clientId}
                locationId={locationId}
                cageId={cageId}
                fishHealthData={fishHealth.welfareData}
                dateFrom={dateFrom}
                dateTo={dateTo}
                onDateChange={onDateChange}
            />
        </PageLayout>
    );
}

export default CageWelfareMaturationContainer;
export { CageWelfareMaturationContainer };
